/* eslint-disable indent */
import classNames from 'classnames'
import { Price } from 'components/common/Price'
import { Separator } from 'components/common/Separator'
import { Currencies, Currency } from 'constants/currencies'
import { TradeFee } from 'hooks/useTradeFees'
import useTranslation from 'next-translate/useTranslation'

type Props = {
  artwork: { name: string; artists: { name: string }[] }
  tradeValue: number
  tradeType?: string
  withArtworkDetails?: boolean
  className?: string
  bodyClassName?: string
  summaryClassName?: string
  tradeFees: TradeFee[]
  total: number
  totalDisclaimer?: string
  userCurrency: Currency
  hideTitle?: boolean
}

const reviewTypes: { [key: string]: string } = {
  sell: 'Sale',
  ask: 'Listing',
  buy: 'Order',
  bid: 'Offer',
  'auction-bid': 'auction-bid',
}

export const TradeBreakdown = (props: Props) => {
  const { t } = useTranslation('review_trade_form')
  const {
    artwork: { name, artists },
    tradeType,
    withArtworkDetails = true,
    hideTitle,
    className,
  } = props

  const reviewType = tradeType ? reviewTypes[tradeType] : ''

  if (!withArtworkDetails)
    return (
      <>
        <TradeBreakdownBody {...props} reviewType={reviewType} />
        <Separator color='cool-300' />
        <TradeBreakdownFooter {...props} />
      </>
    )

  let title = null
  if (tradeType !== 'bid' && tradeType !== 'buy') {
    title = `${t('review')} ${reviewType}`
  } else if (tradeType === 'bid') {
    title = t('offer_details')
  } else if (tradeType === 'buy') {
    title = t('order_details')
  }

  return (
    <>
      {!hideTitle && (
        <h5 className='mb-2 flex items-center font-body text-xl font-medium tracking-normal'>
          {title}
        </h5>
      )}

      <div
        className={classNames(
          className,
          'mb-5 flex flex-col space-y-3 rounded border border-cool-300 bg-cool-50 px-5 py-4'
        )}
      >
        {tradeType === 'bid' ||
        tradeType === 'buy' ||
        tradeType === 'auction-bid' ? (
          <header className='flex items-center'>
            <h6 className='font-body text-base font-medium tracking-normal text-cool-1100'>
              <span>{artists.map(artist => artist.name).join(', ')}</span>
              <span className='mx-2 text-cool-400'> | </span>
              <span className='font-normal'>{name}</span>
            </h6>
          </header>
        ) : (
          <header>
            <h6 className='font-body text-lg font-medium tracking-normal'>
              {name}
            </h6>
            <p className='text-sm text-cool-600'>
              {artists.map(artist => artist.name).join(', ')}
            </p>
          </header>
        )}

        <Separator color='cool-300' />
        <TradeBreakdownBody {...props} reviewType={reviewType} />
        <Separator color='cool-300' />
        <TradeBreakdownFooter {...props} />
      </div>
    </>
  )
}

function TradeBreakdownBody({
  bodyClassName,
  reviewType,
  tradeType,
  userCurrency,
  tradeValue,
  tradeFees,
}: Props & { reviewType: string }) {
  const { t } = useTranslation('review_trade_form')

  const formattingOptions = {
    minimumFractionDigits: 2,
    from: userCurrency,
    to: userCurrency,
  } as const

  let subtotalText = (
    <>
      {reviewType} {t('price')}
    </>
  )

  if (
    reviewType.toLowerCase() === 'order' ||
    reviewType.toLowerCase() === 'auction-bid'
  ) {
    subtotalText = t('subtotal')
  }

  return (
    <div className={bodyClassName}>
      <p className='flex justify-between text-sm font-medium'>
        {subtotalText}

        <Price
          data-e2e={`${tradeType}-value`}
          formattingOptions={formattingOptions}
        >
          {tradeValue}
        </Price>
      </p>

      {tradeFees.map((tradeFee, index) => (
        <p key={index} className='flex justify-between text-xs text-cool-600'>
          <span>{tradeFee.name}</span>

          {typeof tradeFee.value === 'number' && (
            <Price
              data-e2e={`${tradeFee.name.toLowerCase().split(' ')[0]}-value`}
              formattingOptions={formattingOptions}
            >
              {tradeFee.value}
            </Price>
          )}

          {typeof tradeFee.value === 'string' && <span>{tradeFee.value}</span>}
        </p>
      ))}
    </div>
  )
}

function TradeBreakdownFooter({
  summaryClassName,
  tradeType,
  userCurrency,
  total,
  totalDisclaimer,
}: Props) {
  const { t } = useTranslation('review_trade_form')

  const formattingOptions = {
    minimumFractionDigits: 2,
    from: userCurrency,
    to: userCurrency,
  } as const

  let title = `${t('total_payout')} ${
    userCurrency === Currencies.USD ? '(USD)' : ''
  }`
  if (
    tradeType === 'bid' ||
    tradeType === 'buy' ||
    tradeType === 'auction-bid'
  ) {
    title = `${t('total')} ${userCurrency === Currencies.USD ? '(USD)' : ''}`
  }

  return (
    <footer>
      <div
        className={classNames(
          summaryClassName,
          'flex items-center justify-between font-medium text-cool-1100'
        )}
      >
        {title}

        <Price
          className='text-lg'
          data-e2e='total-value'
          formattingOptions={formattingOptions}
        >
          {total}
        </Price>
      </div>

      {totalDisclaimer && (
        <p className='ml-auto w-max text-xs text-cool-600'>{totalDisclaimer}</p>
      )}
    </footer>
  )
}
